/**
 * ContactSelector - Selector de contactos para agregar a una campaña
 * Incluye filtros, selección múltiple e infinite scroll
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { campaignsAPI } from '../../services/api'
import BulkAddToCampaignPicker from './BulkAddToCampaignPicker'

export default function ContactSelector({ campaignId, onEnrollComplete }) {
  // Estado de contactos
  const [contacts, setContacts] = useState([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [error, setError] = useState(null)

  // Estado de filtros
  const [filters, setFilters] = useState({
    search: '',
    assignedTo: '',
    clasificacion: '',
    dateFrom: '',
    dateTo: '',
    createdFrom: '',
    createdTo: '',
    department: '',
    label: '',
    fromCampaign: '', // Filtrar por participación en otra campaña
    folder: '' // Filtrar por lista TodoList
  })
  const [debouncedSearch, setDebouncedSearch] = useState('')

  // Listas para filtros (agentes, departamentos, etiquetas, campañas, listas TodoList)
  const [agents, setAgents] = useState([])
  const [departments, setDepartments] = useState([])
  const [labels, setLabels] = useState([])
  const [campaigns, setCampaigns] = useState([])
  const [folders, setFolders] = useState([])

  // Estado de selección
  const [selectedIds, setSelectedIds] = useState(new Set())
  const [selectAll, setSelectAll] = useState(false)
  const [loadingAllIds, setLoadingAllIds] = useState(false)

  // Estado de inscripción
  const [enrolling, setEnrolling] = useState(false)
  const [showCohortModal, setShowCohortModal] = useState(false)

  // Infinite scroll
  const limit = 50
  const scrollContainerRef = useRef(null)
  const sentinelRef = useRef(null)

  // Cargar agentes, departamentos, etiquetas, campañas y listas al montar
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const [agentsData, departmentsData, labelsData, campaignsData, foldersData] = await Promise.all([
          campaignsAPI.getWassengerAgents(),
          campaignsAPI.getWassengerDepartments(),
          campaignsAPI.getWassengerLabels(),
          campaignsAPI.getCampaigns(),
          campaignsAPI.getFolders()
        ])
        setAgents(agentsData.agents || [])
        setDepartments(departmentsData.departments || [])
        setLabels(labelsData.labels || [])
        // Filtrar para no mostrar la campaña actual
        setCampaigns((campaignsData.campaigns || []).filter(c => c.id !== campaignId))
        setFolders(foldersData.folders || [])
      } catch (err) {
        console.error('Error cargando opciones de filtros:', err)
      }
    }
    loadFilterOptions()
  }, [campaignId])

  // Debounce para búsqueda
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(filters.search)
    }, 300)
    return () => clearTimeout(timer)
  }, [filters.search])

  // Referencia para el offset actual (evita dependencias en useCallback)
  const currentOffsetRef = useRef(0)

  // Cargar contactos (con soporte para infinite scroll)
  const loadContacts = useCallback(async (append = false) => {
    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
        currentOffsetRef.current = 0
      }
      setError(null)

      const offset = append ? currentOffsetRef.current : 0

      const data = await campaignsAPI.getAvailableContacts(campaignId, {
        search: debouncedSearch,
        assignedTo: filters.assignedTo,
        clasificacion: filters.clasificacion,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        createdFrom: filters.createdFrom,
        createdTo: filters.createdTo,
        department: filters.department,
        label: filters.label,
        fromCampaign: filters.fromCampaign,
        folder: filters.folder,
        limit,
        offset
      })

      const newContacts = data.contacts || []
      const newTotal = data.total || 0

      if (append) {
        setContacts(prev => {
          const updated = [...prev, ...newContacts]
          currentOffsetRef.current = updated.length
          return updated
        })
      } else {
        setContacts(newContacts)
        currentOffsetRef.current = newContacts.length
        setSelectAll(false)
      }

      setTotal(newTotal)
      setHasMore(offset + newContacts.length < newTotal)
    } catch (err) {
      console.error('Error cargando contactos:', err)
      if (!append) {
        setError('Error al cargar contactos')
      }
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }, [campaignId, debouncedSearch, filters.assignedTo, filters.clasificacion, filters.dateFrom, filters.dateTo, filters.createdFrom, filters.createdTo, filters.department, filters.label, filters.fromCampaign, filters.folder])

  // Cargar contactos iniciales cuando cambian filtros
  useEffect(() => {
    loadContacts(false)
  }, [loadContacts])

  // Reset selección cuando cambian filtros
  useEffect(() => {
    setSelectedIds(new Set())
  }, [debouncedSearch, filters.assignedTo, filters.clasificacion, filters.dateFrom, filters.dateTo, filters.createdFrom, filters.createdTo, filters.department, filters.label, filters.fromCampaign, filters.folder])

  // Infinite scroll con IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          loadContacts(true)
        }
      },
      {
        root: scrollContainerRef.current,
        rootMargin: '100px',
        threshold: 0
      }
    )

    observer.observe(sentinelRef.current)

    return () => observer.disconnect()
  }, [hasMore, loadingMore, loading, loadContacts])

  // Handlers de filtros
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }))
  }

  const clearFilters = () => {
    setFilters({
      search: '',
      assignedTo: '',
      clasificacion: '',
      dateFrom: '',
      dateTo: '',
      createdFrom: '',
      createdTo: '',
      department: '',
      label: '',
      fromCampaign: '',
      folder: ''
    })
    setContacts([])
  }

  // Handlers de selección
  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev)
      if (newSet.has(id)) {
        newSet.delete(id)
      } else {
        newSet.add(id)
      }
      return newSet
    })
  }

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(contacts.map(c => c.id)))
    }
    setSelectAll(!selectAll)
  }

  // Seleccionar TODOS los filtrados (todas las páginas)
  const selectAllFiltered = async () => {
    try {
      setLoadingAllIds(true)
      const result = await campaignsAPI.getAllAvailableContactIds(campaignId, {
        search: debouncedSearch,
        assignedTo: filters.assignedTo,
        clasificacion: filters.clasificacion,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
        createdFrom: filters.createdFrom,
        createdTo: filters.createdTo,
        department: filters.department,
        label: filters.label,
        fromCampaign: filters.fromCampaign,
        folder: filters.folder
      })

      if (result.ids && result.ids.length > 0) {
        setSelectedIds(new Set(result.ids))
        setSelectAll(true)
      }
    } catch (err) {
      console.error('Error cargando todos los IDs:', err)
      alert('Error al seleccionar todos los contactos')
    } finally {
      setLoadingAllIds(false)
    }
  }

  // Inscribir seleccionados - abre BulkAddToCampaignPicker con campaña pre-seleccionada
  const handleEnrollSelected = () => {
    if (selectedIds.size === 0) return;
    setShowCohortModal(true);
  }

  // Formatear fecha
  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('es-CL', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit'
    })
  }

  // Obtener badge de clasificación - Estilo Koomo
  const getClasificacionBadge = (clasificacion) => {
    const config = {
      A: { bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', dot: 'bg-emerald-500' },
      B: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', dot: 'bg-blue-500' },
      C: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', dot: 'bg-amber-500' },
      D: { bg: 'bg-gray-100', text: 'text-gray-600', border: 'border-gray-200', dot: 'bg-gray-400' }
    }
    const c = config[clasificacion] || config.D
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${c.bg} ${c.text} border ${c.border}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${c.dot}`}></span>
        {clasificacion || '-'}
      </span>
    )
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Barra de filtros - Estilo Koomo */}
      <div className="bg-white border-b border-gray-200 p-5 space-y-4 shadow-sm">
        {/* Fila 1: Búsqueda */}
        <div className="flex gap-3">
          <div className="flex-1 relative">
            <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              placeholder="Buscar por nombre o telefono..."
              className="w-full pl-11 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm transition"
            />
          </div>
          <button
            onClick={clearFilters}
            className="px-4 py-2.5 text-gray-500 hover:bg-gray-100 rounded-xl text-sm font-medium transition"
            title="Limpiar filtros"
          >
            Limpiar
          </button>
        </div>

        {/* Filtro destacado: De campaña anterior */}
        {campaigns.length > 0 && (
          <div className="flex gap-3 items-center p-3 bg-primary-50 rounded-xl border border-primary-200">
            <label className="text-sm font-medium text-primary-700">De campana:</label>
            <select
              value={filters.fromCampaign}
              onChange={(e) => handleFilterChange('fromCampaign', e.target.value)}
              className="flex-1 px-4 py-2 border border-primary-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 bg-white font-medium"
            >
              <option value="">-- Todos los contactos --</option>
              {campaigns.map((camp) => (
                <option key={camp.id} value={camp.id}>
                  {camp.name} ({camp.enrolled_count || 0} inscritos)
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Fila 2: Filtros adicionales */}
        <div className="flex gap-2 flex-wrap">
          <select
            value={filters.clasificacion}
            onChange={(e) => handleFilterChange('clasificacion', e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 bg-white transition"
          >
            <option value="">Clasificacion</option>
            <option value="A">A - Caliente</option>
            <option value="B">B - Tibio</option>
            <option value="C">C - Frio</option>
            <option value="D">D - Descartado</option>
          </select>

          <select
            value={filters.assignedTo}
            onChange={(e) => handleFilterChange('assignedTo', e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 bg-white transition"
          >
            <option value="">Agente asignado</option>
            {agents.map((agent) => (
              <option key={agent} value={agent}>{agent}</option>
            ))}
          </select>

          {departments.length > 0 && (
            <select
              value={filters.department}
              onChange={(e) => handleFilterChange('department', e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 bg-white transition"
            >
              <option value="">Departamento</option>
              {departments.map((dept) => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
          )}

          {labels.length > 0 && (
            <select
              value={filters.label}
              onChange={(e) => handleFilterChange('label', e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-primary-500 bg-white transition"
            >
              <option value="">Etiqueta</option>
              {labels.map((label) => (
                <option key={label.id} value={`wss:${label.id}`}>{label.name}</option>
              ))}
            </select>
          )}

          {folders.length > 0 && (
            <select
              value={filters.folder}
              onChange={(e) => handleFilterChange('folder', e.target.value)}
              className="px-4 py-2 border border-purple-300 rounded-xl text-sm focus:ring-2 focus:ring-purple-500 bg-purple-50 transition"
            >
              <option value="">Lista TodoList</option>
              {folders.filter(f => !f.archivada).map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.icono || '📁'} {folder.nombre} ({folder.contact_count})
                </option>
              ))}
            </select>
          )}

          <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-xl">
            <span className="font-medium">Actividad:</span>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={(e) => handleFilterChange('dateFrom', e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
            />
            <span>a</span>
            <input
              type="date"
              value={filters.dateTo}
              onChange={(e) => handleFilterChange('dateTo', e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>

        {/* Fila 3: Filtro de fecha de creación */}
        <div className="flex gap-3 flex-wrap">
          <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-xl">
            <span className="font-medium">Creacion:</span>
            <input
              type="date"
              value={filters.createdFrom}
              onChange={(e) => handleFilterChange('createdFrom', e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
            />
            <span>a</span>
            <input
              type="date"
              value={filters.createdTo}
              onChange={(e) => handleFilterChange('createdTo', e.target.value)}
              className="px-2 py-1 border border-gray-300 rounded-lg text-sm"
            />
          </div>
        </div>

        {/* Barra de acciones */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm cursor-pointer font-medium text-gray-700">
              <input
                type="checkbox"
                checked={selectAll && selectedIds.size === contacts.length}
                onChange={toggleSelectAll}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              Pagina
            </label>

            {/* Botón para seleccionar TODOS los filtrados */}
            {total > limit && (
              <button
                onClick={selectAllFiltered}
                disabled={loadingAllIds || selectedIds.size === total}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loadingAllIds ? 'Cargando...' : selectedIds.size === total ? `Todos (${total})` : `Seleccionar todos (${total})`}
              </button>
            )}

            <span className="text-sm text-gray-500">
              {selectedIds.size > 0 ? `${selectedIds.size} seleccionados` : `${total} disponibles`}
            </span>

            {selectedIds.size > 0 && (
              <button
                onClick={() => { setSelectedIds(new Set()); setSelectAll(false) }}
                className="text-sm text-gray-500 hover:text-gray-700 font-medium"
              >
                Limpiar
              </button>
            )}
          </div>

          <button
            onClick={handleEnrollSelected}
            disabled={selectedIds.size === 0 || enrolling}
            className="px-5 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl hover:from-primary-700 hover:to-primary-800 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm font-medium shadow-md"
          >
            {enrolling ? 'Agregando...' : `Agregar ${selectedIds.size > 0 ? `(${selectedIds.size})` : ''}`}
          </button>
        </div>
      </div>

      {/* Lista de contactos - Estilo Koomo con Infinite Scroll */}
      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto p-5">
        {loading && contacts.length === 0 ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
        ) : error ? (
          <div className="p-4 text-center text-red-600 bg-red-50 rounded-xl">{error}</div>
        ) : contacts.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl shadow-sm">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">🔍</span>
            </div>
            <p className="text-gray-600 font-medium">No se encontraron contactos disponibles</p>
            <p className="text-sm text-gray-400 mt-1">Ajusta los filtros o todos los contactos ya estan inscritos</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <table className="min-w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={toggleSelectAll}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Contacto</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Clasificacion</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Agente</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Creacion</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Ultima actividad</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    className={`hover:bg-gray-50 cursor-pointer transition ${selectedIds.has(contact.id) ? 'bg-primary-50' : ''}`}
                    onClick={() => toggleSelect(contact.id)}
                  >
                    <td className="px-4 py-4">
                      <input
                        type="checkbox"
                        checked={selectedIds.has(contact.id)}
                        onChange={() => toggleSelect(contact.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                      />
                    </td>
                    <td className="px-4 py-4">
                      <div>
                        <p className="text-sm font-medium text-gray-900">{contact.nombre || 'Sin nombre'}</p>
                        <p className="text-xs text-gray-400">{contact.telefono}</p>
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      {getClasificacionBadge(contact.clasificacion)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-600">
                      {contact.wassenger_asignado_a || '-'}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {formatDate(contact.creado_en)}
                    </td>
                    <td className="px-4 py-4 text-sm text-gray-500">
                      {formatDate(contact.ultimo_mensaje)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Sentinel para infinite scroll */}
            <div ref={sentinelRef} className="h-4" />

            {/* Indicador de carga de más contactos */}
            {loadingMore && (
              <div className="flex items-center justify-center py-6">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mr-3"></div>
                <span className="text-sm text-gray-500">Cargando mas contactos...</span>
              </div>
            )}

            {/* Indicador de fin de lista */}
            {!hasMore && contacts.length > 0 && (
              <div className="text-center py-4 text-sm text-gray-400">
                Mostrando {contacts.length} de {total} contactos
              </div>
            )}
          </div>
        )}
      </div>
      {showCohortModal && (
        <BulkAddToCampaignPicker
          contactIds={Array.from(selectedIds)}
          preselectedCampaignId={campaignId}
          onClose={() => setShowCohortModal(false)}
          onSuccess={(result) => {
            setShowCohortModal(false);
            alert(`✅ ${result.enrolled} prospectos inscritos`);
            setSelectedIds(new Set());
            setSelectAll(false);
            setContacts([]);
            if (onEnrollComplete) onEnrollComplete(result);
          }}
        />
      )}
    </div>
  )
}
